import {clamp} from '@benchling/js-utils';

import * as analytics from 'analytics/Analytics';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModal from 'file-browser/FolderSelectorModal';
import ProxyFile, {ProxyFileAttributes, ProxyFileSetOptions} from 'folder-items/ProxyFile';
import {IsContainedInComplexPolymerData} from 'modbio/complex-polymers/ComplexPolymerConstants';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceDAO from 'molbio/aa-sequences/AaSequenceDAO';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {
  AaSequenceServerResponse as AaSequenceType,
  AaSequenceAnnotationVersion,
  AaSequencePart,
} from 'molbio/aa-sequences/util/AaSequenceConstants';
import CodonOptimizationTabState from 'molbio/codon-optimization/CodonOptimizationTabState';
import {Selection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {selectRange} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';

interface AaSequenceAttributes extends ProxyFileAttributes {
  protein: AaSequenceType;
  selection: Selection;
  id: string;
  // Whether the model is a read-only representation of an AA sequence outside of the usual AA sequence workspace context
  isTransientDisplay?: boolean;
  // For a transient display, the AA sequence it represents. Its own id is a placeholder, and it carries no tag
  // schema.
  transientSource?: {entityId: string; tagSchemaId: string | null};
}

interface AaSequence {
  get<K extends keyof AaSequenceAttributes>(key: K): AaSequenceAttributes[K];
  set<K extends keyof AaSequenceAttributes>(
    attributeName: K,
    value: AaSequenceAttributes[K],
    options?: ProxyFileSetOptions
  ): AaSequence;
  set(obj: Partial<AaSequenceAttributes>, options?: ProxyFileSetOptions): AaSequence;
}

class AaSequence extends ProxyFile<AaSequenceType, typeof AaSequenceStore> {
  static staticInit(): void {
    this.prototype.proxiedField = 'protein';
  }

  override initialize(): void {
    super.initialize();
    this.set('selection', new Selection({sequence: this}), {bypassProxyBlock: true});
    this.on('change:protein', () => {
      this.clampSelection();
    });
  }

  override usesAutoSave(): boolean {
    return false;
  }

  selection(): Selection {
    return this.get('selection');
  }

  getStore(): typeof AaSequenceStore {
    return AaSequenceStore;
  }

  getState(): {protein: AaSequenceType} {
    return {protein: this.getStore().getAaSequenceById(this.id)!};
  }

  aminoAcids(): string {
    return this.proxied().version.aminoAcids;
  }

  annotations(): Array<AaSequenceAnnotationVersion> {
    return this.proxied().version.annotations;
  }

  parts(): Array<AaSequencePart> {
    return this.proxied().version.parts ?? [];
  }

  length(): number {
    return this.aminoAcids().length;
  }

  fragments(): Array<{
    start: number;
    end: number;
    color: string;
  }> {
    return this.proxied().version.fragments ?? [];
  }

  load({deferred}: {versionId?: number | null; deferred: JQueryDeferred<void>}): void {
    AaSequenceActions.open(this.id, {deferred, view: true});
  }

  override setTags(tags: AaSequenceType['tags']): void {
    AaSequenceActions.postServerUpdate(this.id, {tags});
  }

  override setTagSchema(tagSchema: AaSequenceType['tagSchema']): void {
    AaSequenceActions.postServerUpdate(this.id, {tagSchema});
  }

  override setAuthors(authors: AaSequenceType['authors']): void {
    AaSequenceActions.postServerUpdate(this.id, {authors});
  }

  override setArchiveRecord(archiveRecord: AaSequenceType['archiveRecord']): void {
    AaSequenceActions.postServerUpdate(this.id, {archiveRecord});
  }

  override setSchemaValidationState(schemaValidationState: AaSequenceType['schemaValidationState']): void {
    AaSequenceActions.postServerUpdate(this.id, {schemaValidationState});
  }

  override setPermissions(permissions: AaSequenceType['permissions']): void {
    AaSequenceActions.postServerUpdate(this.id, {permissions});
  }

  isTransientDisplay(): boolean {
    return this.get('isTransientDisplay') ?? false;
  }

  override isLatest(): boolean {
    const proxied = this.proxied();
    return proxied && proxied.isLatest;
  }

  tagSchemaId(): string | null {
    const transientSource = this.get('transientSource');
    return transientSource != null ? transientSource.tagSchemaId : (this.proxied().tagSchema?.id ?? null);
  }

  /**
   * The API id of the AA sequence this model represents, which a transient display's own id is not.
   */
  entityId(): string {
    return this.get('transientSource')?.entityId ?? this.id;
  }

  override writable(): boolean {
    return this.permissions().writable;
  }

  override allowEdits(): boolean {
    return this.writable() && this.isLatest();
  }

  allowFeatureEdits(): boolean | undefined {
    return this.permissions().polymerFeaturesEditable && this.isLatest() && !this.isTransientDisplay();
  }

  isContainedInComplexPolymerData(): IsContainedInComplexPolymerData | null {
    const proxied = this.proxied();
    if (proxied && proxied.isContainedInComplexPolymerData) {
      return proxied.isContainedInComplexPolymerData;
    }
    return null;
  }

  allowResidueEdits(): boolean {
    return (
      this.permissions().polymerResiduesEditable! &&
      this.isLatest() &&
      !this.isContainedInComplexPolymerData()?.isContained &&
      !this.isTransientDisplay()
    );
  }

  isSelectionEditable(): boolean {
    return this.getPartsInSelection(this.selection().start(), this.selection().end()).length === 0;
  }

  canInsertAt(position: number): boolean {
    return this.getPartsInSelection(position, null).length === 0;
  }

  isSelectionDeletable(): boolean {
    const selection = this.selection();
    return selection.isNotEmpty() && this.canInsertAt(selection.start()) && this.canInsertAt(selection.end());
  }

  circular(): boolean {
    return false;
  }

  /**
   * When the protein residues change, clamp the selection state to the ends of the sequence if it is no longer valid.
   */
  clampSelection(): void {
    const clampIndex = (index: number | null): number | null =>
      index == null ? null : clamp(0, this.length(), index);
    const [currentStart, currentEnd] = this.selection().getEndpoints();
    const clampedStart = clampIndex(currentStart);
    const clampedEnd = clampIndex(currentEnd);
    selectRange(this.id, clampedStart, clampedEnd);
  }

  residueSlice(start: number, end: number): string {
    return sequtil.residueSlice(this.aminoAcids(), start, end, false);
  }

  getPartsBetween(start: number, end: number): Array<AaSequencePart> {
    return this.parts().filter((part) => sequtil.isRangeOverlap(part.start, part.end, start, end));
  }

  getPartsInSelection(selectionStart: number, selectionEnd: number | null): Array<AaSequencePart> {
    if (selectionEnd == null) {
      const position = selectionStart === this.length() ? 0 : selectionStart;
      return this.parts().filter((part) => sequtil.baseInExclusiveRange(position, part.start, part.end));
    } else {
      return this.getPartsBetween(selectionStart, selectionEnd);
    }
  }

  handleDoubleClick(regionStart: number, regionEnd: number): void {
    const index = this.selection().start();
    const partsAtIndex = this.getPartsInSelection(index, null);
    if (index !== this.length() && partsAtIndex.length === 1) {
      const [partToSelect] = partsAtIndex;
      this.selection().setEndpoints(partToSelect.start, partToSelect.end);
    } else {
      // Default behavior is to select the whole "region" (e.g. slice)
      this.selection().reset(regionStart);
      this.selection().setEndpoints(regionStart, regionEnd);
    }
  }

  startIndex(): number {
    return 1;
  }

  isSingleStranded(): boolean {
    return true;
  }

  deleteSelection(): void {
    AaSequenceActions.deleteAminoAcids(
      this.id,
      {
        start: this.selection().start(),
        end: this.selection().end()!,
      },
      () => {
        selectRange(this.id, this.selection().start(), null);
      }
    );
  }

  override schemaValidationState(): SchemaValidationState | null {
    return this.get('protein').schemaValidationState;
  }

  async handleBackTranslate(): Promise<void> {
    const aaSelection = this.residueSlice(this.selection().start(), this.selection().end()!);
    const reverseTranslation = await AaSequenceDAO.reverseTranslateSelection(aaSelection);
    const tab = new CodonOptimizationTabState({bases: reverseTranslation.bases, protein: this});
    this.fileState().buffers().addTab(tab);
    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_CODON_OPTIMIZATION, {polymerId: this.get('id')});
  }

  duplicate(): void {
    FolderSelectorModal.openForCopy(
      {
        initialSelectedFolderId: this.folder()!.api_identifier,
        showInventory: true,
        showNotebook: false,
      },
      (folderId) => {
        FileBrowserActions.copyAndOpenItem(this.id, folderId);
      }
    );
  }

  getActiveVersionId(): number {
    return this.get('protein').version.id;
  }
}
AaSequence.staticInit();

export default AaSequence;
